/* Day Trading → 💼 Portfolio tab (Schwab).

   3-state UI driven by GET /api/schwab/status:
     (a) configured=false  → "Not yet available" + roadmap
     (b) configured=true, connected=false → "Connect Schwab" CTA
     (c) connected=true    → dashboard of accounts + positions + realised P/L
                             + auto-journal poll (5-min) that writes filled
                             orders to the Journal with localStorage dedupe. */

import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { C } from "./_shared";

const API = process.env.REACT_APP_BACKEND_URL || "";
const JOURNALLED_KEY = "schwab_journalled_orders";
const AUTO_JOURNAL_INTERVAL = 5 * 60 * 1000;  // 5 minutes

async function jfetch(path, init = {}) {
  const res = await fetch(`${API}${path}`, { credentials: "include", ...init });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

// ── Order helpers ─────────────────────────────────────
// Schwab returns nested {orderLegCollection[], orderActivityCollection[]}.
// Flatten a single order doc into its first execution leg for table display.
function flattenOrder(o) {
  const legs = Array.isArray(o.orderLegCollection) ? o.orderLegCollection : [];
  const acts = Array.isArray(o.orderActivityCollection) ? o.orderActivityCollection : [];
  const firstLeg = legs[0] || {};
  // Pull executed price/qty from activity collection when present
  let executedPrice = 0, executedQty = 0;
  for (const act of acts) {
    const execs = act.executionLegs || [];
    for (const ex of execs) { executedPrice = ex.price ?? executedPrice; executedQty += (ex.quantity ?? 0); }
  }
  return {
    orderId:   o.orderId || o.activityId || o.orderNumber || null,
    ticker:    firstLeg.instrument?.symbol || "—",
    side:      firstLeg.instruction || "—",     // BUY | SELL | BUY_TO_OPEN | SELL_TO_CLOSE
    qty:       executedQty || firstLeg.quantity || 0,
    price:     executedPrice || o.price || 0,
    enteredTime: o.enteredTime || o.closeTime || null,
    status:    o.status || "—",
  };
}

function isTodayET(iso) {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    const todayET = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    const dayET   = d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    return todayET === dayET;
  } catch { return false; }
}

export default function PortfolioTab({ requestAnalyse }) {
  const [status,    setStatus]    = useState(null);   // {configured, connected, ...}
  const [accounts,  setAccounts]  = useState(null);
  const [positions, setPositions] = useState(null);
  const [orders,    setOrders]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [autoJournalCount, setAutoJournalCount] = useState(0);

  // Pick up the ?schwab=connected success bounce from the OAuth callback.
  const [justConnected] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("schwab") === "connected"; }
    catch { return false; }
  });

  // ── Auto-journal: POST filled orders to /api/trading/journal,
  // dedupe by orderId in localStorage so the same fill never
  // gets journalled twice across page reloads. ──
  const autoJournalFills = useCallback(async (filledOrders) => {
    let alreadyJournalled = new Set();
    try {
      const raw = localStorage.getItem(JOURNALLED_KEY);
      if (raw) alreadyJournalled = new Set(JSON.parse(raw));
    } catch { /* noop */ }
    let newCount = 0;
    for (const o of filledOrders) {
      const oid = String(o.orderId || "");
      if (!oid || alreadyJournalled.has(oid)) continue;
      try {
        const r = await fetch(`${API}/api/trading/journal`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ticker:    o.ticker,
            direction: /SELL/i.test(o.side) ? "Short" : "Long",
            entry:     Number(o.price) || 0,
            shares:    Number(o.qty) || 0,
            session:   "Schwab fill",
            notes:     `Auto-journalled Schwab fill · ${o.side} ${o.qty} @ $${o.price} · order ${oid}`,
            entered_at: o.enteredTime,
          }),
        });
        if (r.ok) {
          alreadyJournalled.add(oid);
          newCount += 1;
        }
      } catch { /* skip on network error; retry next interval */ }
    }
    if (newCount > 0) {
      try { localStorage.setItem(JOURNALLED_KEY, JSON.stringify([...alreadyJournalled])); }
      catch { /* noop */ }
    }
    return newCount;
  }, []);

  const syncOrders = useCallback(async () => {
    try {
      const data = await jfetch("/api/schwab/orders?days=1");
      const list = Array.isArray(data) ? data : [];
      const flat = list.map(flattenOrder).filter(o => o.orderId);
      setOrders(flat);
      setLastSyncAt(new Date());
      // Auto-journal: only FILLED orders from today
      const todaysFills = flat.filter(o => /FILLED/i.test(o.status) && isTodayET(o.enteredTime));
      if (todaysFills.length > 0) {
        const n = await autoJournalFills(todaysFills);
        if (n > 0) setAutoJournalCount(c => c + n);
      }
    } catch (e) {
      // Silent — order history is an enrichment, not a blocker
    }
  }, [autoJournalFills]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const s = await jfetch("/api/schwab/status");
        if (cancelled) return;
        setStatus(s);
        if (s.connected) {
          const [a, p] = await Promise.allSettled([
            jfetch("/api/schwab/accounts"),
            jfetch("/api/schwab/positions"),
          ]);
          if (!cancelled) {
            setAccounts(a.status === "fulfilled" ? a.value : null);
            setPositions(p.status === "fulfilled" ? p.value : null);
          }
          // Kick off the first orders sync immediately
          if (!cancelled) await syncOrders();
        }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [justConnected, syncOrders]);

  // 5-minute poll for new fills while the tab is open.
  useEffect(() => {
    if (!status?.connected) return;
    const id = setInterval(() => { syncOrders(); }, AUTO_JOURNAL_INTERVAL);
    return () => clearInterval(id);
  }, [status?.connected, syncOrders]);

  function startOAuth() {
    window.location.href = `${API}/api/schwab/auth/login`;
  }
  async function disconnect() {
    if (!window.confirm("Disconnect Schwab? You'll need to re-authenticate to reconnect.")) return;
    await jfetch("/api/schwab/disconnect", { method: "POST" });
    window.location.reload();
  }

  if (loading) {
    return (
      <div data-testid="portfolio-tab-loading" style={{ padding: 32, textAlign: "center", color: C.gray, fontSize: 12 }}>
        Loading Schwab status…
      </div>
    );
  }

  // ── STATE A: Schwab not configured on this server ──
  if (!status?.configured) {
    return (
      <div data-testid="portfolio-tab" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{
          background: "linear-gradient(135deg,#1e1b4b,#312e81)", color: "#fff",
          borderRadius: 10, padding: "20px 24px", position: "relative", overflow: "hidden",
        }} data-testid="portfolio-not-configured">
          <div style={{ position: "absolute", top: -30, right: -30, fontSize: 160, opacity: 0.07 }}>💼</div>
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
              💼 Charles Schwab Portfolio — Coming Soon
            </div>
            <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.6, marginBottom: 12, maxWidth: 740 }}>
              Connect your Schwab brokerage account to see live positions, real-time P/L, real options Greeks in the
              Trade Planner, and auto-journaled order fills. Set{" "}
              <code style={{ background: "rgba(255,255,255,0.1)", padding: "1px 5px", borderRadius: 3 }}>SCHWAB_CLIENT_ID</code>{" "}
              + <code style={{ background: "rgba(255,255,255,0.1)", padding: "1px 5px", borderRadius: 3 }}>SCHWAB_CLIENT_SECRET</code>{" "}
              on the server to activate this tab.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="portfolio-feature-pills">
              {[
                { icon: "💼", l: "Live account balance + buying power" },
                { icon: "📊", l: "All open positions with real-time P/L" },
                { icon: "🎯", l: "Real options Greeks in Trade Planner" },
                { icon: "🤖", l: "Auto-journal fills every 5 min" },
                { icon: "💰", l: "Today's realised P/L" },
              ].map((f, i) => (
                <span key={i} style={{
                  fontSize: 11, padding: "4px 10px", borderRadius: 5,
                  background: "rgba(255,255,255,0.12)", color: "#e0e7ff", fontWeight: 600,
                }}>{f.icon} {f.l}</span>
              ))}
            </div>
          </div>
        </div>
        <div style={{
          background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8,
          padding: "10px 14px", fontSize: 11, color: "#78350f", lineHeight: 1.5,
        }}>
          🔒 <b>Read-only Phase 1 — no order placement.</b> This integration reads account data,
          positions, order history, and options chains only.
        </div>
      </div>
    );
  }

  // ── STATE B: configured, not yet connected OR reconnect required ──
  if (!status?.connected) {
    const needsReconnect = status.reconnect_required;
    return (
      <div data-testid="portfolio-tab" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{
          background: "#fff", border: `2px dashed ${needsReconnect ? C.red : C.indigo}`,
          borderRadius: 10, padding: 32, textAlign: "center",
        }} data-testid="portfolio-connect-cta">
          <div style={{ fontSize: 40, marginBottom: 8 }}>💼</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#111", marginBottom: 6 }}>
            {needsReconnect ? "Schwab session expired" : "Connect your Schwab account"}
          </div>
          <div style={{ fontSize: 12, color: C.gray, lineHeight: 1.6, maxWidth: 520, margin: "0 auto 16px" }}>
            {needsReconnect
              ? "Your Schwab refresh token has expired (7-day window). Reconnect to resume live positions and auto-journal."
              : "See live positions, real options Greeks in the Trade Planner, and auto-journaled fills. OAuth flow — you'll log in at schwab.com, grant read-only access, and be redirected back here."}
          </div>
          <button
            onClick={startOAuth}
            data-testid="portfolio-connect-btn"
            style={{
              padding: "10px 24px", borderRadius: 6, border: "none", cursor: "pointer",
              background: needsReconnect ? "#dc2626" : "#4f46e5", color: "#fff",
              fontSize: 13, fontWeight: 600, display:"inline-flex", alignItems:"center", gap:6,
            }}
          >
            {needsReconnect ? "🔄 Reconnect Schwab" : "🔐 Connect Charles Schwab →"}
          </button>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 16 }} data-testid="portfolio-feature-pills">
            {[
              "Live account balance + buying power",
              "All open positions with real-time P/L",
              "Real options Greeks in Trade Planner",
              "Auto-journal fills every 5 min",
              "Today's realised P/L",
            ].map((l, i) => (
              <span key={i} style={{
                fontSize: 10, padding: "3px 8px", borderRadius: 4,
                background: C.ib, color: C.it, fontWeight: 600,
              }}>{l}</span>
            ))}
          </div>
          <div style={{
            background: C.ab, border: `1px solid ${C.amber}55`, color: "#78350f",
            borderRadius: 6, padding: "8px 12px", fontSize: 10, fontWeight: 600,
            marginTop: 16, lineHeight: 1.5, maxWidth: 520, marginLeft: "auto", marginRight: "auto",
          }}>
            🔒 Read-only Phase 1 — no order placement. This integration reads account data,
            positions, order history, and options chains only.
          </div>
        </div>
      </div>
    );
  }

  // ── STATE C: Connected — live dashboard ──
  return (
    <div data-testid="portfolio-tab" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10,
        padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#111" }}>
            <span style={{ color: "#16a34a", marginRight: 6 }}>●</span>
            Connected to Charles Schwab
          </div>
          <div style={{ fontSize: 10, color: C.gray, marginTop: 2 }}>
            {status.connected_at && `Since ${new Date(status.connected_at).toLocaleString()}`}
            {status.account_hash && <> · Account hash cached</>}
          </div>
        </div>
        <button
          onClick={disconnect}
          data-testid="portfolio-disconnect-btn"
          style={{
            padding: "6px 12px", borderRadius: 6, border: "0.5px solid #e5e7eb",
            background: "#fff", color: C.gray, fontSize: 11, fontWeight: 600, cursor: "pointer",
          }}
        >Disconnect Schwab</button>
      </div>
      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 7, padding: "8px 12px", fontSize: 11, color: "#991b1b" }}>
          ⚠ {error}
        </div>
      )}
      <AccountsCard data={accounts}/>
      <PositionsCard data={positions} requestAnalyse={requestAnalyse}/>
      <RealisedPnLCard orders={orders} lastSyncAt={lastSyncAt} autoJournalCount={autoJournalCount}/>
      <div style={{ fontSize: 10, color: C.gray, textAlign: "center", fontStyle: "italic", padding: 8 }}>
        Read-only — no order placement. Auto-journal polls every 5 minutes.
      </div>
    </div>
  );
}

function AccountsCard({ data }) {
  const accounts = Array.isArray(data) ? data : [];
  if (accounts.length === 0) {
    return (
      <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "12px 16px", fontSize: 11, color: C.gray }}>
        No accounts returned — check Schwab app permissions.
      </div>
    );
  }
  // Build the 5-card account summary bar from the first account.
  const acct = accounts[0]?.securitiesAccount || accounts[0] || {};
  const bal  = acct.currentBalances || acct.initialBalances || {};
  const liq  = Number(bal.liquidationValue || bal.equity || 0);
  const bp   = Number(bal.buyingPower || 0);
  const dayPL = Number(bal.dayProfitLoss ?? acct.dayProfitLoss ?? 0);
  const longMV = Number(bal.longMarketValue || 0);
  const costBasis = Number(bal.costBasis || acct.aggregatedBalance?.currentLiquidationValue || 0);
  const totalPL = longMV - costBasis;
  const cards = [
    { label: "Net liquidation value", value: `$${liq.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`, color: "#111" },
    { label: "Cash & buying power",   value: `$${bp.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`, color: "#111" },
    { label: "Day P&L",               value: `${dayPL >= 0 ? "+" : ""}$${Math.abs(dayPL).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`, color: dayPL >= 0 ? C.green : C.red },
    { label: "Total P&L",             value: `${totalPL >= 0 ? "+" : ""}$${Math.abs(totalPL).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`, color: totalPL >= 0 ? C.green : C.red },
    { label: "Account type",          value: acct.type || "—", color: "#111" },
  ];
  return (
    <div data-testid="portfolio-accounts" style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, color: C.gray, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>Account Summary</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        {cards.map(c => (
          <div key={c.label} data-testid={`portfolio-metric-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
               style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: C.gray, fontWeight: 600 }}>{c.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: c.color, marginTop: 2, fontFamily: "monospace" }}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PositionsCard({ data, requestAnalyse }) {
  const accounts = Array.isArray(data) ? data : [];
  const positions = accounts.flatMap(a => (a.securitiesAccount?.positions) || []);
  const sortedPositions = useMemo(() => {
    return positions.slice().sort((a, b) => {
      const qtyA = (a.longQuantity || 0) - (a.shortQuantity || 0);
      const qtyB = (b.longQuantity || 0) - (b.shortQuantity || 0);
      const mvA  = Number(a.marketValue || 0);
      const mvB  = Number(b.marketValue || 0);
      const costA = Number(a.averagePrice || 0) * qtyA;
      const costB = Number(b.averagePrice || 0) * qtyB;
      const totalA = Math.abs(mvA - costA);
      const totalB = Math.abs(mvB - costB);
      return totalB - totalA;
    });
  }, [positions]);

  return (
    <div data-testid="portfolio-positions" style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, color: C.gray, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>
        Open Positions <span style={{ color: "#111", fontWeight: 700 }}>({sortedPositions.length})</span>
      </div>
      {sortedPositions.length === 0 ? (
        <div style={{ fontSize: 11, color: C.gray, fontStyle: "italic" }}>No open positions.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 760 }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Ticker", "Qty", "Avg cost", "Current price", "Market value", "Day P&L", "Total P&L", "P&L %"].map(h => (
                  <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: C.gray, fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPositions.map((p, i) => {
                const sym = p.instrument?.symbol || "—";
                const qty = (p.longQuantity || 0) - (p.shortQuantity || 0);
                const avg = Number(p.averagePrice || 0);
                const mv  = Number(p.marketValue || 0);
                const cur = qty !== 0 ? mv / qty : 0;
                const dayPL = Number(p.currentDayProfitLoss || 0);
                const cost = avg * qty;
                const totalPL = mv - cost;
                const plp = cost !== 0 ? (totalPL / Math.abs(cost)) * 100 : 0;
                const dayProfit = dayPL >= 0, totalProfit = totalPL >= 0;
                const clickable = typeof requestAnalyse === "function" && sym !== "—";
                return (
                  <tr
                    key={i}
                    data-testid={`portfolio-row-${sym}`}
                    onClick={() => clickable && requestAnalyse(sym)}
                    style={{ borderTop: "0.5px solid #f3f4f6", cursor: clickable ? "pointer" : "default" }}
                    onMouseEnter={(e) => { if (clickable) e.currentTarget.style.background = "#fafbff"; }}
                    onMouseLeave={(e) => { if (clickable) e.currentTarget.style.background = "transparent"; }}
                  >
                    <td style={{ padding: "6px 10px", fontWeight: 800, color: clickable ? C.indigo : "#111" }}>{sym}</td>
                    <td style={{ padding: "6px 10px" }}>{qty.toFixed(0)}</td>
                    <td style={{ padding: "6px 10px" }}>${avg.toFixed(2)}</td>
                    <td style={{ padding: "6px 10px" }}>${cur.toFixed(2)}</td>
                    <td style={{ padding: "6px 10px" }}>${mv.toFixed(2)}</td>
                    <td style={{ padding: "6px 10px", fontWeight: 700, color: dayProfit ? C.green : C.red }}>
                      {dayProfit ? "+" : "-"}${Math.abs(dayPL).toFixed(2)}
                    </td>
                    <td style={{ padding: "6px 10px", fontWeight: 700, color: totalProfit ? C.green : C.red }}>
                      {totalProfit ? "+" : "-"}${Math.abs(totalPL).toFixed(2)}
                    </td>
                    <td style={{ padding: "6px 10px", fontWeight: 600, color: totalProfit ? C.green : C.red }}>
                      {totalProfit ? "+" : ""}{plp.toFixed(2)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Today's realised P&L card ────────────────────────────
// Aggregates today's FILLED orders into a running realised-P&L total.
// FIFO-ish: a SELL realises (price - avg cost) × qty. We don't have
// the average cost basis at sell-time in /orders, so the per-trade
// "value" column shows gross trade size (qty × price). Running total
// at top sums BUY/SELL signs so the user gets a directional read.
function RealisedPnLCard({ orders, lastSyncAt, autoJournalCount }) {
  const todaysOrders = useMemo(() => {
    return orders
      .filter(o => /FILLED/i.test(o.status) && isTodayET(o.enteredTime))
      .sort((a, b) => new Date(a.enteredTime || 0) - new Date(b.enteredTime || 0));
  }, [orders]);

  const runningTotal = useMemo(() => {
    let total = 0;
    for (const o of todaysOrders) {
      const value = Number(o.qty) * Number(o.price);
      // SELL realises positive cash flow, BUY is negative
      total += /SELL/i.test(o.side) ? value : -value;
    }
    return total;
  }, [todaysOrders]);

  const totalSign = runningTotal >= 0 ? "+" : "-";
  return (
    <div data-testid="portfolio-realised" style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: C.gray, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Today's realised P&L
          </div>
          <div data-testid="portfolio-realised-total" style={{
            fontSize: 26, fontWeight: 800, color: runningTotal >= 0 ? C.green : C.red,
            marginTop: 4, fontFamily: "monospace",
          }}>
            {totalSign}${Math.abs(runningTotal).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}
          </div>
          <div style={{ fontSize: 10, color: C.gray, marginTop: 2 }}>
            {todaysOrders.length} fill{todaysOrders.length === 1 ? "" : "s"} today
          </div>
        </div>
      </div>

      {todaysOrders.length === 0 ? (
        <div style={{ fontSize: 11, color: C.gray, fontStyle: "italic", marginTop: 10 }}>
          No fills today yet — orders auto-sync every 5 minutes.
        </div>
      ) : (
        <div style={{ overflowX: "auto", marginTop: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 520 }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Time", "Ticker", "Side", "Qty", "Price", "Total value"].map(h => (
                  <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: C.gray, fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {todaysOrders.map((o, i) => {
                const t = o.enteredTime ? new Date(o.enteredTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
                const isSell = /SELL/i.test(o.side);
                const value = Number(o.qty) * Number(o.price);
                return (
                  <tr key={o.orderId || i} data-testid={`portfolio-fill-${o.orderId || i}`} style={{ borderTop: "0.5px solid #f3f4f6" }}>
                    <td style={{ padding: "6px 10px", color: C.gray }}>{t}</td>
                    <td style={{ padding: "6px 10px", fontWeight: 800, color: "#111" }}>{o.ticker}</td>
                    <td style={{ padding: "6px 10px", fontWeight: 700, color: isSell ? C.red : C.green }}>{o.side}</td>
                    <td style={{ padding: "6px 10px" }}>{Number(o.qty).toFixed(0)}</td>
                    <td style={{ padding: "6px 10px" }}>${Number(o.price).toFixed(2)}</td>
                    <td style={{ padding: "6px 10px", fontWeight: 700, color: isSell ? C.green : C.red }}>${value.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div data-testid="portfolio-autojournal-status" style={{ fontSize: 9, color: C.muted, marginTop: 10, textAlign: "right" }}>
        Auto-journal: last synced {lastSyncAt ? lastSyncAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} · {todaysOrders.length} fill{todaysOrders.length === 1 ? "" : "s"} today
        {autoJournalCount > 0 && ` · ${autoJournalCount} new journal entr${autoJournalCount === 1 ? "y" : "ies"}`}
      </div>
    </div>
  );
}
